import { useMemo } from "react";
import { HyperliquidConnectionStatus } from "@cede/types";
import type { useHyperliquidConnection } from "./useHyperliquidConnection";
import type { HyperliquidSessionStatus } from "@/components/features/perps/types";
import { WALLET_STATUS, type WalletStatus } from "@/constants/wallet";
import type { useSocialAccountRequirement } from "@/hooks/socials/useSocialAccountRequirement";

interface UseHyperliquidSessionStatusProps {
  connection: ReturnType<typeof useHyperliquidConnection>;
  authenticated?: boolean;

  /**
   * Wallet status from useHubUser (preferred approach)
   * When provided, the hook uses this for wallet connection checks
   * and does not require the auth parameter.
   */
  walletStatus?: WalletStatus;

  /**
   * Social account requirement hook result (campaign-specific)
   * If provided, adds social account checks to session status
   */
  socialRequirement?: ReturnType<typeof useSocialAccountRequirement>;

  /**
   * Whether to check social requirements
   * Default: false (trade page), true when socialRequirement is provided
   */
  checkSocialRequirements?: boolean;
}

/**
 * Unified session status hook for Hyperliquid trading
 *
 * Consolidates session status logic for HyperliquidTradingWidget
 * and TradingDrawer/MobileTradingLayout.
 *
 * Uses walletStatus from useHubUser for clean wallet state detection.
 *
 * @example Usage
 * ```tsx
 * const { walletStatus } = useHubUser();
 * const sessionStatus = useHyperliquidSessionStatus({
 *   connection,
 *   walletStatus,
 *   socialRequirement, // Optional: for campaign pages
 *   checkSocialRequirements: true, // Optional: for campaign pages
 * });
 * ```
 */
export function useHyperliquidSessionStatus({
  connection,
  authenticated,
  walletStatus,
  socialRequirement,
  checkSocialRequirements = !!socialRequirement,
}: UseHyperliquidSessionStatusProps): HyperliquidSessionStatus {
  const status = connection.agentData.status;

  return useMemo(() => {
    // Use walletStatus from useHubUser for auth state (matches navbar behavior)
    if (walletStatus === WALLET_STATUS.INITIALIZING || walletStatus === WALLET_STATUS.CONNECTING) {
      return "checking-wallet";
    }
    if (walletStatus === WALLET_STATUS.DISCONNECTED && authenticated !== true) {
      return "needs-wallet";
    }
    if (authenticated === false) {
      return "needs-wallet";
    }

    // Check eligibility
    // TODO: Re-enable eligibility check after testing
    // if (connection.isCheckingEligibility) {
    //   return "checking-eligibility";
    // }

    // // Explicitly check for eligible === true, not just truthy
    // if (connection.eligibilityData?.eligible !== true) {
    //   return "not-eligible";
    // }

    // Wait for agent data to load before making status decisions.
    // Without this, DEFAULT_AGENT_DATA (status: WAITING_APPROVAL) would
    // cause the UI to show "Approve Agent Wallet" even when data is still
    // loading or when the query errored (e.g. eligibility rejection).
    if (connection.isLoadingAgent || connection.isCheckingEligibility) {
      return "checking-eligibility";
    }

    // Show "not eligible" only for explicit eligibility failures.
    if (connection.isNotEligibleError || connection.eligibilityData?.eligible === false) {
      return "not-eligible";
    }

    if (!connection.hasAgentData) {
      // Query finished but returned no data for a non-eligibility reason.
      // Keep checking state to avoid showing a false "not eligible" status.
      return "checking-eligibility";
    }

    // Check agent approval status
    if (status === HyperliquidConnectionStatus.WAITING_APPROVAL) {
      return "needs-approval";
    }

    // Check account preparation (deposit required)
    if (status === HyperliquidConnectionStatus.WAITING_DEPOSIT) {
      return "needs-preparation";
    }

    // Check activation status
    if (connection.isUpdatingAgentStatus) {
      return "checking-activation";
    }

    // Check social account requirements (campaign-specific)
    if (checkSocialRequirements && socialRequirement) {
      if (socialRequirement.isLoading) {
        return "checking-socials";
      }
      if (socialRequirement.shouldBlockTrading) {
        return "needs-social-accounts";
      }
    }

    // All checks passed
    return "ready";
  }, [
    walletStatus,
    authenticated,
    connection.isLoadingAgent,
    connection.isCheckingEligibility,
    connection.hasAgentData,
    connection.isNotEligibleError,
    connection.eligibilityData?.eligible,
    connection.isUpdatingAgentStatus,
    status,
    checkSocialRequirements,
    socialRequirement?.isLoading,
    socialRequirement?.shouldBlockTrading,
  ]);
}
